import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import React, { CSSProperties, useEffect, useState } from 'react';
import { Tag } from 'primereact/tag';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Users,
  Percent,
  Target,
} from 'lucide-react';
import {
  ProgramBreakdownFinancialIncomeReport,
  StudentPaymentSummary,
} from '../../../../../api/types/reports/finanace/program-breakdown/incomes/program-financial-breakdown-incomes.type';
import { UserSummaryType } from '../../../../../api/types/user/user-summary.type';
import { numberToHebrewLetter } from '../../../../../utils/hebrew-date-utils';
import Colors from '../../../../../utils/colors';

interface Dataset {
  type: string;
  label: string;
  backgroundColor: string;
  data: (number | null)[];
  stack?: string;
}

interface AsdData {
  title: string;
  str: string;
  icon?: React.ReactNode;
}

const Asd: React.FC<AsdData> = ({ str, title, icon }) => {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        padding: '24px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: 0,
        minHeight: '120px',
        flex: 1,
        gap: '24px',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <span
          style={{
            fontSize: '1.1rem',
            color: '#64748b',
            fontWeight: 500,
            marginBottom: 8,
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: '2rem',
            fontWeight: 700,
            color: '#0f172a',
            letterSpacing: '-1px',
          }}
        >
          {str}
        </span>
      </div>
      <div
        style={{
          background: 'linear-gradient(135deg, #f3f4f6 0%, #e0e7ef 100%)',
          borderRadius: '50%',
          width: 56,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        }}
      >
        {icon}
      </div>
    </div>
  );
};

// ProgramBreakdownFinancialIncomeReport

export interface ProgramFinancialIncomesDatamodel {
  data: ProgramBreakdownFinancialIncomeReport;
  onUserClick: (user: UserSummaryType) => any;
}

interface YearOption {
  id: number;
  label: string;
}

// Custom Year Filter Component
const YearFilterChips: React.FC<{
  yearOptions: YearOption[];
  selectedYears: number[];
  onToggleYear: (yearId: number) => void;
}> = ({ yearOptions, selectedYears, onToggleYear }) => {
  const chipStyle = (isSelected: boolean): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 16px',
    margin: '4px',
    borderRadius: '20px',
    border: isSelected ? 'none' : '2px solid #e2e8f0',
    background: isSelected
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : '#fff',
    color: isSelected ? '#fff' : '#64748b',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: isSelected
      ? '0 4px 15px rgba(102, 126, 234, 0.3)'
      : '0 2px 8px rgba(0,0,0,0.06)',
    transform: isSelected ? 'translateY(-1px)' : 'translateY(0)',
    userSelect: 'none' as const,
  });

  const containerStyle: CSSProperties = {
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '32px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    direction: 'rtl',
    textAlign: 'right',
  };

  const headerStyle: CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '16px',
    textAlign: 'right',
    direction: 'rtl',
  };

  const chipsContainerStyle: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start', // Start from right in RTL context
    direction: 'rtl',
    textAlign: 'right',
  };

  const allSelectedButtonStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    margin: '4px',
    borderRadius: '16px',
    border: '1px solid #cbd5e1',
    background: '#f8fafc',
    color: '#475569',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    userSelect: 'none' as const,
    direction: 'rtl',
  };

  const handleSelectAll = () => {
    if (selectedYears.length === yearOptions.length) {
      // Deselect all
      yearOptions.forEach((year) => {
        if (selectedYears.includes(year.id)) {
          onToggleYear(year.id);
        }
      });
    } else {
      // Select all
      yearOptions.forEach((year) => {
        if (!selectedYears.includes(year.id)) {
          onToggleYear(year.id);
        }
      });
    }
  };

  const isAllSelected = selectedYears.length === yearOptions.length;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>סינון לפי שנים</div>
      <div style={chipsContainerStyle}>
        <div
          style={allSelectedButtonStyle}
          onClick={handleSelectAll}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#e2e8f0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f8fafc';
          }}
        >
          {isAllSelected ? 'בטל הכל' : 'בחר הכל'}
        </div>
        {yearOptions.map((year) => {
          const isSelected = selectedYears.includes(year.id);
          return (
            <div
              key={year.id}
              style={chipStyle(isSelected)}
              onClick={() => onToggleYear(year.id)}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow =
                    '0 4px 12px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 2px 8px rgba(0,0,0,0.06)';
                }
              }}
            >
              {year.label}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ProgramFinancialIncomes: React.FC<
  ProgramFinancialIncomesDatamodel
> = ({ data, onUserClick }) => {
  const [selectedYears, setSelectedYears] = useState<number[]>([]);

  const style1 = {
    textAlign: 'right',
    maxWidth: '120px',
    minWidth: '120px',
  } as CSSProperties;

  // Create year options from the available data
  const yearOptions: YearOption[] = React.useMemo(() => {
    if (!data) return [];

    const availableYears = Object.keys(data).map(Number).sort();
    return availableYears.map((year) => ({
      id: year,
      label: `שנה ${numberToHebrewLetter(year)}׳`,
    }));
  }, [data]);

  // Initialize selected years to include all available years
  React.useEffect(() => {
    if (yearOptions.length > 0 && selectedYears.length === 0) {
      setSelectedYears(yearOptions.map((year) => year.id));
    }
  }, [yearOptions]);

  const toggleYear = (yearId: number) => {
    setSelectedYears((prev) =>
      prev.includes(yearId)
        ? prev.filter((id) => id !== yearId)
        : [...prev, yearId],
    );
  };

  // Filter data based on selected years
  const filteredData = React.useMemo(() => {
    if (!data || selectedYears.length === 0) return {};

    const filtered: ProgramBreakdownFinancialIncomeReport = {};
    selectedYears.forEach((year) => {
      if (data[year]) {
        filtered[year] = data[year];
      }
    });
    return filtered;
  }, [data, selectedYears]);

  // Aggregate students by userId (or name if userId is not available) from filtered data
  const studentMap = new Map<string, StudentPaymentSummary>();

  if (filteredData) {
    Object.values(filteredData)
      .flat()
      .forEach((student) => {
        // Use userId from the first paymentService, or fallback to name
        const userId = student.paymentServices[0]?.userId;
        const key = userId
          ? String(userId)
          : `${student.firstName} ${student.lastName}`;

        if (!studentMap.has(key)) {
          // Clone the student object to avoid mutating the original
          studentMap.set(key, {
            ...student,
            paymentServices: [...student.paymentServices],
          });
        } else {
          // Merge paymentServices
          const existing = studentMap.get(key)!;
          existing.paymentServices.push(...student.paymentServices);
        }
      });
  }

  const students = Array.from(studentMap.values());

  // Compute summary stats from income data
  let totalExpectedIncome = 0;
  let totalPaid = 0;
  let totalOwed = 0;
  let numDebtorStudents = 0;
  let paidToExpectedRatio = 0;
  const totalStudents = students.length;

  students.forEach((student) => {
    const expectedForStudent = student.paymentServices.reduce(
      (sum, service) => sum + (service.price || 0),
      0,
    );
    const paidForStudent = student.paymentServices.reduce(
      (sum, service) =>
        sum +
        (service.userPayments?.reduce((p, pay) => p + pay.paymentAmount, 0) ||
          0),
      0,
    );

    totalExpectedIncome += expectedForStudent;
    totalPaid += paidForStudent;

    if (paidForStudent < expectedForStudent) {
      numDebtorStudents++;
    }
  });

  totalOwed = totalExpectedIncome - totalPaid;

  if (totalExpectedIncome > 0) {
    paidToExpectedRatio = Math.round((totalPaid / totalExpectedIncome) * 100);
  }

  // Prepare chart data from students
  const chartLabels = students.map(
    (student) => `${student.firstName} ${student.lastName}`,
  );
  // Example: paid = sum of all payments, owed = sum of all prices - paid
  const paidAmounts = students.map((student) => {
    return student.paymentServices.reduce((sum, service) => {
      return (
        sum +
        (service.userPayments?.reduce((p, pay) => p + pay.paymentAmount, 0) ||
          0)
      );
    }, 0);
  });
  const totalAmounts = students.map((student) => {
    return student.paymentServices.reduce(
      (sum, service) => sum + (service.price || 0),
      0,
    );
  });
  const owedAmounts = totalAmounts.map((total, i) => total - paidAmounts[i]);

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        type: 'bar',
        label: 'שולם',
        backgroundColor: Colors.actualIncomesBackground,
        borderColor: Colors.actualIncomesBorder,
        borderWidth: 1,
        data: paidAmounts,
      },
      {
        type: 'bar',
        label: 'חייב',
        backgroundColor: Colors.expectedExpensesBackground,
        borderColor: Colors.expectedExpensesBorder,
        borderWidth: 1,
        data: owedAmounts,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    aspectRatio: 0.8,
    datasets: {
      bar: {
        categoryPercentage: 0.1, // Use full category width
        barPercentage: 2.5, // Use full bar width
      },
    },
    plugins: {
      tooltip: {
        mode: 'index',
        intersect: false,
      },
      legend: {
        labels: {
          color: '#333',
          usePointStyle: true,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#666',
        },
        grid: {
          color: '#eee',
        },
      },
      y: {
        ticks: {
          color: '#666',
        },
        grid: {
          color: '#eee',
        },
      },
    },
    barPercentage: 0.6,
    categoryPercentage: 0.8,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Year Filter */}
      {yearOptions.length > 0 && (
        <YearFilterChips
          yearOptions={yearOptions}
          selectedYears={selectedYears}
          onToggleYear={toggleYear}
        />
      )}

      {/* Income Summary Cards */}
      <div style={{ marginBottom: '48px' }}>
        <h2
          style={{
            textAlign: 'right',
            marginBottom: '24px',
            color: '#374151',
            fontSize: '1.5rem',
            fontWeight: 600,
          }}
        >
          הכנסות
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '24px',
            marginBottom: '32px',
          }}
        >
          <Asd
            str={`${totalExpectedIncome.toLocaleString()}₪`}
            title={'סה״כ הכנסות צפויות'}
            icon={<Target color="#3b82f6" size={32} />}
          />
          <Asd
            str={`${totalOwed.toLocaleString()}₪`}
            title={'סה״כ חייבים'}
            icon={<XCircle color="#ef4444" size={32} />}
          />
          <Asd
            str={`${totalPaid.toLocaleString()}₪`}
            title={'סה״כ שולם'}
            icon={<CheckCircle color="#22c55e" size={32} />}
          />
          <Asd
            str={`${numDebtorStudents}`}
            title={'מספר סטודנטים חייבים'}
            icon={<Users color="#3b82f6" size={32} />}
          />
          <Asd
            str={`${paidToExpectedRatio}%`}
            title={'יחס שולם/צפי הכנסות'}
            icon={<Percent color="#a21caf" size={32} />}
          />
        </div>
      </div>

      <Chart type="bar" data={chartData} options={chartOptions} />

      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        value={students}
        onRowClick={(e) => onUserClick(e.data as UserSummaryType)}
      >
        <Column
          header=""
          field={''}
          body={() => <Checkbox checked={true} />}
          style={{ width: '100px', textAlign: 'right' }}
        />
        <Column
          header="שם"
          field={''}
          body={(student: StudentPaymentSummary) => (
            <div>
              {student.firstName} {student.lastName}
            </div>
          )}
          style={style1}
        />
        <Column
          header="שולם"
          field={''}
          body={(student: StudentPaymentSummary) => {
            const paid = student.paymentServices.reduce(
              (sum, service) =>
                sum +
                (service.userPayments?.reduce(
                  (p, pay) => p + pay.paymentAmount,
                  0,
                ) || 0),
              0,
            );
            return `${paid.toLocaleString()}₪`;
          }}
          style={style1}
        />
        <Column
          header="חייב"
          field={''}
          body={(student: StudentPaymentSummary) => {
            const total = student.paymentServices.reduce(
              (sum, service) => sum + (service.price || 0),
              0,
            );
            const paid = student.paymentServices.reduce(
              (sum, service) =>
                sum +
                (service.userPayments?.reduce(
                  (p, pay) => p + pay.paymentAmount,
                  0,
                ) || 0),
              0,
            );
            const owed = total - paid;
            return `${owed.toLocaleString()}₪`;
          }}
          style={style1}
        />
        <Column
          header="יחס שולם/צפי הכנסות"
          field={''}
          body={(student: StudentPaymentSummary) => {
            const total = student.paymentServices.reduce(
              (sum, service) => sum + (service.price || 0),
              0,
            );
            const paid = student.paymentServices.reduce(
              (sum, service) =>
                sum +
                (service.userPayments?.reduce(
                  (p, pay) => p + pay.paymentAmount,
                  0,
                ) || 0),
              0,
            );
            const ratio = total > 0 ? Math.round((paid / total) * 100) : 0;
            return `${ratio}%`;
          }}
          style={style1}
        />
        <Column
          header="סטטוס"
          field={''}
          body={(student: StudentPaymentSummary) => {
            const total = student.paymentServices.reduce(
              (sum, service) => sum + (service.price || 0),
              0,
            );
            const paid = student.paymentServices.reduce(
              (sum, service) =>
                sum +
                (service.userPayments?.reduce(
                  (p, pay) => p + pay.paymentAmount,
                  0,
                ) || 0),
              0,
            );
            if (paid === 0)
              return (
                <Tag
                  style={{
                    width: '85px',
                  }}
                  severity="warning"
                  value="לא שולם"
                />
              );
            if (paid < total)
              return (
                <Tag
                  style={{
                    width: '85px',
                  }}
                  severity="info"
                  value="שולם חלקית"
                />
              );
            return (
              <Tag
                style={{
                  width: '85px',
                }}
                severity="success"
                value="שולם"
              />
            );
          }}
          style={style1}
        />
      </DataTable>
    </div>
  );
};
